import { LocaleAnchor, LocaleImage } from '@/i18n/router';
import { useMessages } from '@/i18n/LocaleProvider';
import messages from '@/i18n/catalogs/corporate/TestimonialSection';

import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import AnimatedSection from './AnimatedSection';
import SectionHeading from '@/components/SectionHeading';
import summationLogo from '@/assets/summation-logo.svg';
import randoliLogo from '@/assets/randoli-logo.svg';
import { quotePhotos } from '@/data/customerQuotes';
import QuoteGloss from '@/components/QuoteGloss';

export interface Testimonial {
  // The approved English wording; ptBR is our translation, shown under it on gizmodata.com.br.
  quote: string;
  ptBR?: string;
  author: string;
  title?: string;
  company: string;
  companyDescription: string;
  companyUrl: string;
  linkedIn?: string;
  photo?: string;
  photoSize?: number;
  logo: string;
  logoWidth?: number;
  logoHeight?: number;
}

type TestimonialMessages = typeof messages.en;

export const getDefaultTestimonials = (m: TestimonialMessages): Testimonial[] => [
  {
    quote: messages.en.s1,
    ptBR: messages['pt-BR'].s1,
    author: 'Hema Nagarajan',
    title: m.s2,
    company: 'Summation',
    companyDescription: m.s3,
    companyUrl: 'https://summation.com',
    linkedIn: 'https://www.linkedin.com/in/hema-nagarajan-7526b462/',
    ...quotePhotos.hema,
    logo: summationLogo,
    logoWidth: 560,
    logoHeight: 88,
  },
  {
    quote: messages.en.s4,
    ptBR: messages['pt-BR'].s4,
    author: 'Rajith Attapattu',
    title: m.s5,
    company: 'Randoli',
    companyDescription: m.s6,
    companyUrl: 'https://randoli.io',
    linkedIn: 'https://www.linkedin.com/in/rajith-muditha-attapattu/',
    ...quotePhotos.rajith,
    logo: randoliLogo,
    logoWidth: 1557,
    logoHeight: 321,
  },
];

const QuoteIcon = () => (
  <svg className="h-8 w-8 text-gizmo-600" fill="currentColor" viewBox="0 0 32 32">
    <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
  </svg>
);

interface TestimonialSectionProps {
  heading?: string;
  subheading?: string;
  testimonials?: Testimonial[];
}

const TestimonialSection: React.FC<TestimonialSectionProps> = ({
  heading,
  subheading,
  testimonials: suppliedTestimonials,
}) => {
  const m = useMessages(messages);
  const testimonials = suppliedTestimonials ?? getDefaultTestimonials(m);
  return (
    <section id="testimonials" className="scroll-mt-24 bg-muted/50 section-padding">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <div className="text-center mb-16">
            <SectionHeading anchor="testimonials" className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {heading ?? m.heading}
            </SectionHeading>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {subheading ?? m.subheading}
            </p>
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {testimonials.map((testimonial, index) => (
            <AnimatedSection key={index} animation="fade-up">
              <Card className="border border-border shadow-md hover:shadow-lg transition-shadow bg-card h-full">
                <CardContent className="p-8">
                  <div className="flex flex-col h-full">
                    <div className="flex items-center justify-between mb-4">
                      <QuoteIcon />
                      <LocaleAnchor href={testimonial.companyUrl} target="_blank" rel="noopener noreferrer">
                        <LocaleImage
                          src={testimonial.logo}
                          alt={testimonial.company}
                          width={testimonial.logoWidth}
                          height={testimonial.logoHeight}
                          className="h-6 w-auto object-contain dark:[filter:drop-shadow(0_0_1px_rgba(255,255,255,0.9))_drop-shadow(0_0_6px_rgba(255,255,255,0.45))]"
                        />
                      </LocaleAnchor>
                    </div>
                    <div className="mb-8 flex-grow text-muted-foreground">
                      <p lang="en" className="leading-relaxed">
                        {testimonial.quote}
                      </p>
                      <QuoteGloss ptBR={testimonial.ptBR} />
                    </div>
                    <hr className="border-border mb-6" />
                    <div className="flex items-center gap-4">
                      {testimonial.photo && (
                        <LocaleImage
                          src={testimonial.photo}
                          alt={testimonial.author}
                          width={testimonial.photoSize}
                          height={testimonial.photoSize}
                          loading="lazy"
                          className="h-14 w-14 shrink-0 rounded-full object-cover"
                        />
                      )}
                      <div>
                        <p className="font-semibold text-foreground">
                          {testimonial.linkedIn ? (
                            <LocaleAnchor
                              href={testimonial.linkedIn}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:text-gizmo-600 transition-colors"
                              data-ga-cta="view_quote_author"
                              data-ga-location="testimonials"
                              data-ga-text={testimonial.author}
                            >
                              {testimonial.author}
                            </LocaleAnchor>
                          ) : (
                            testimonial.author
                          )}
                        </p>
                        {testimonial.title && (
                          <p className="text-sm text-muted-foreground">{testimonial.title}</p>
                        )}
                        <p className="text-sm text-muted-foreground mt-1">
                          <LocaleAnchor
                            href={testimonial.companyUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-gizmo-600 hover:text-gizmo-700 transition-colors"
                          >
                            {testimonial.company}
                          </LocaleAnchor>
                          {' \u2014 '}
                          {testimonial.companyDescription}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialSection;
